<template>
  <div
    class="media-gallery"
    :class="{ 'media-gallery--stacked': isStacked }"
    :style="rootStyle"
    tabindex="0"
    @keydown.left.prevent="prev"
    @keydown.right.prevent="next"
  >
    <!-- ============ THUMBNAIL COLUMN / STRIP ============ -->
    <div
      v-if="normalizedItems.length > 1 && this.$vuetify.breakpoint.mdAndUp"
      ref="thumbs"
      class="media-gallery__thumbs"
      :style="thumbsStyle"
    >
      <button
        v-for="(item, i) in normalizedItems"
        :key="i"
        ref="thumbEls"
        type="button"
        class="media-gallery__thumb"
        :class="{ 'is-active': i === activeIndex }"
        :style="thumbItemStyle"
        :aria-label="`Show item ${i + 1}`"
        :aria-current="i === activeIndex"
        @mouseenter="onThumbHover(i)"
        @click="select(i)"
        @focus="select(i)"
      >
        <v-img
          :src="item.thumb"
          :alt="item.alt"
          aspect-ratio="1"
          class="media-gallery__thumb-img"
        >
          <template #placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular
                indeterminate
                size="18"
                width="2"
                color="grey lighten-1"
              />
            </v-row>
          </template>
        </v-img>

        <!-- play badge for video thumbnails -->
        <v-icon
          v-if="item.type === 'video'"
          class="media-gallery__thumb-play"
          dark
        >
          mdi-play-circle
        </v-icon>
      </button>
    </div>

    <!-- ================== MAIN STAGE ================== -->
    <div class="media-gallery__main" :style="mainStyle">
      <v-fade-transition mode="out-in">
        <!-- A single keyed wrapper so switching always tears down the previous
             media (important: destroys the VideoEmbed iframe and stops playback). -->
        <div :key="stageKey" class="media-gallery__stage-item">
          <!-- VIDEO (YouTube / Vimeo via the shared VideoEmbed component) -->
          <VideoEmbed
            v-if="active.type === 'video'"
            :src="String(active.src)"
            :title="active.alt || 'Video'"
            :controls="videoControls"
            :autoplay="videoAutoplay"
            :loop="videoLoop"
            fill
            class="media-gallery__media"
          />
          <!-- IMAGE -->
          <v-img
            v-else
            :src="active.src"
            :alt="active.alt"
            contain
            class="media-gallery__media"
          >
            <template #placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="grey lighten-1" />
              </v-row>
            </template>
          </v-img>
        </div>
      </v-fade-transition>

      <!-- arrows + counter -->
      <template v-if="normalizedItems.length > 1">
        <v-btn
          class="media-gallery__arrow media-gallery__arrow--prev white--text font-weight-bold"
          rounded
          :small="dense"
          aria-label="Previous"
          @click="prev"
        >
          <v-icon>mdi-chevron-left</v-icon>
          {{ activeIndex + 1 }} / {{ normalizedItems.length }}
        </v-btn>

        <v-btn
          class="media-gallery__arrow media-gallery__arrow--next"
          icon
          :small="dense"
          aria-label="Next"
          @click="next"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </template>
    </div>
  </div>
</template>

<script>
// Adjust this path if your VideoEmbed lives elsewhere.
import VideoEmbed from "@/components/global-components/VideoEmbed.vue";

export default {
  name: "MediaGallery",

  components: { VideoEmbed },

  props: {
    /**
     * Array of media. Each entry may be:
     *   - a string  -> treated as an image src
     *   - an object -> { type: 'image'|'video', src, thumb|thumbnail, alt }
     *
     * For videos, `src` is the platform video ID (numeric => Vimeo, else
     * YouTube) exactly as VideoEmbed expects, and `thumbnail` is the poster.
     * `thumb`/`thumbnail` fall back to `src` when omitted (images).
     */
    items: {
      type: Array,
      required: true,
    },
    // aspect ratio of the main stage (width / height). 1 = square.
    aspectRatio: {
      type: [Number, String],
      default: 1,
    },
    // optional hard cap on the stage height (px). Mirrors the old 450px cap.
    maxStageHeight: {
      type: Number,
      default: null,
    },
    // width of the thumbnail rail on desktop (px)
    thumbSize: {
      type: Number,
      default: 72,
    },
    // gap between rail and stage, and between thumbs (px)
    gap: {
      type: Number,
      default: 12,
    },
    // hard cap on the component width so it can be sized per-instance.
    maxWidth: {
      type: [Number, String],
      default: null,
    },
    // wrap around when going past the ends
    loop: {
      type: Boolean,
      default: true,
    },
    // smaller arrows for compact instances
    dense: {
      type: Boolean,
      default: false,
    },
    // forces the stacked (mobile) layout regardless of breakpoint
    stacked: {
      type: Boolean,
      default: false,
    },
    // ---- video behaviour (passed through to VideoEmbed) ----
    videoControls: {
      type: Boolean,
      default: true,
    },
    videoAutoplay: {
      type: Boolean,
      default: false,
    },
    videoLoop: {
      type: Boolean,
      default: false,
    },
    // ---- thumbnail highlight colours ----
    activeColor: {
      type: String,
      default: "var(--v-primary-base, #1976d2)",
    },
    // empty string => reuse activeColor for hover
    hoverColor: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      activeIndex: 0,
      // true on devices that actually support hover (desktop with a mouse)
      canHover: true,
    };
  },

  computed: {
    normalizedItems() {
      return (this.items || []).map((raw) => {
        const o = typeof raw === "string" ? { src: raw } : { ...raw };
        return {
          type: o.type || "image",
          src: o.src,
          thumb: o.thumb || o.thumbnail || o.src,
          alt: o.alt || "",
        };
      });
    },

    active() {
      return this.normalizedItems[this.activeIndex] || {};
    },

    // changing this key forces the stage to fully re-render on switch
    stageKey() {
      return `${this.active.type}-${this.activeIndex}-${this.active.src || ""}`;
    },

    isStacked() {
      return this.stacked;
    },

    rootStyle() {
      const s = {
        "--mg-active": this.activeColor,
        "--mg-hover": this.hoverColor || this.activeColor,
      };
      if (this.maxWidth != null) {
        s.maxWidth =
          typeof this.maxWidth === "number"
            ? `${this.maxWidth}px`
            : this.maxWidth;
      }
      return s;
    },

    mainStyle() {
      const s = { aspectRatio: String(Number(this.aspectRatio)) };
      if (this.maxStageHeight) s.maxHeight = `${this.maxStageHeight}px`;
      return s;
    },

    thumbsStyle() {
      return this.isStacked
        ? { gap: `${this.gap}px`, marginTop: `${this.gap}px` }
        : {
            width: `${this.thumbSize}px`,
            gap: `${this.gap}px`,
            marginRight: `${this.gap}px`,
          };
    },

    thumbItemStyle() {
      // on the horizontal strip the thumbs need an explicit width
      return this.isStacked ? { width: `${this.thumbSize}px` } : {};
    },
  },

  watch: {
    // keep the active thumbnail visible in the rail when navigating
    activeIndex() {
      this.$nextTick(this.scrollActiveIntoView);
    },
    // clamp when the source list changes at runtime
    normalizedItems(items) {
      if (this.activeIndex > items.length - 1) {
        this.activeIndex = Math.max(0, items.length - 1);
      }
    },
  },

  mounted() {
    if (typeof window !== "undefined" && window.matchMedia) {
      this.canHover = window.matchMedia("(hover: hover)").matches;
    }
  },

  methods: {
    select(i) {
      if (i < 0 || i >= this.normalizedItems.length || i === this.activeIndex)
        return;
      this.activeIndex = i;
      this.$emit("change", { index: i, item: this.active });
    },

    onThumbHover(i) {
      // hover-to-switch only on real hover devices; touch uses tap (@click)
      if (this.canHover) this.select(i);
    },

    next() {
      const last = this.normalizedItems.length - 1;
      if (this.activeIndex < last) this.select(this.activeIndex + 1);
      else if (this.loop) this.select(0);
    },

    prev() {
      if (this.activeIndex > 0) this.select(this.activeIndex - 1);
      else if (this.loop) this.select(this.normalizedItems.length - 1);
    },

    scrollActiveIntoView() {
      const els = this.$refs.thumbEls;
      const el = els && els[this.activeIndex];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    },
  },
};
</script>

<style scoped>
.media-gallery {
  display: flex;
  align-items: stretch;
  width: 100%;
  outline: none;
}

/* ---- thumbnail rail (desktop) ---- */
.media-gallery__thumbs {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
}
.media-gallery__thumbs::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.media-gallery__thumbs::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.18);
  border-radius: 4px;
}

.media-gallery__thumb {
  position: relative;
  flex: 0 0 auto;
  padding: 2;
  border: 2px solid transparent;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  background: #f5f5f5;
  transition: border-color 0.15s ease, opacity 0.15s ease;
  opacity: 0.75;
}
.media-gallery__thumb.is-active {
  opacity: 1;
  border-color: var(--mg-active);
}
/* hover rule placed AFTER active so hover colour wins, matching the
   original "yellow over pink" behaviour */
.media-gallery__thumb:hover,
.media-gallery__thumb:focus-visible {
  opacity: 1;
  border-color: var(--mg-hover);
}
.media-gallery__thumb-img {
  width: 100%;
}
.media-gallery__thumb-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 26px !important;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  pointer-events: none;
}

/* ---- main stage ---- */
.media-gallery__main {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  border-radius: 4px;
  overflow: hidden;
  background: #ffffff;
}
.media-gallery__stage-item {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
}
.media-gallery__media {
  width: 100%;
  height: 100%;
  padding: 7px;
}

.media-gallery__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  background: rgba(0, 0, 0, 0.45) !important;
}
.media-gallery__arrow .v-icon {
  color: #fff;
}
.media-gallery__arrow--prev {
  left: 8px;
}
.media-gallery__arrow--next {
  right: 8px;
}
.media-gallery__arrow:hover {
  background: rgba(0, 0, 0, 0.7) !important;
}

.media-gallery__counter {
  position: absolute;
  bottom: 8px;
  right: 10px;
  z-index: 3;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  line-height: 1.4;
  color: #fff;
  background: rgba(0, 0, 0, 0.5);
  pointer-events: none;
}

/* ---- stacked / mobile layout: strip BELOW the stage ---- */
.media-gallery--stacked {
  flex-direction: column;
}
.media-gallery--stacked .media-gallery__thumbs {
  flex-direction: row;
  width: 100% !important;
  margin-right: 0 !important;
  overflow-x: auto;
  overflow-y: hidden;
}
</style>
